import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  XCircle,
  User,
  Phone,
  FileText,
  CreditCard,
  CheckSquare,
  Square,
  PlusCircleIcon,
} from "lucide-react";
import SearchInput from "../Search/SearchInput";
import {
  getAllRepayments,
  updateBulkRepayments,
  updateRepayment,
  updateRepaymentCustomField,
} from "../../store/slices/commissionAndRepaymentSlice";
import {
  ICustomField,
  IRepayment,
  IRepaymentHistoryItem,
  IRepaymentLog,
  IStatusCycle,
} from "../../models/commissionAndRepayment.model";
import toast from "react-hot-toast";
import RejectRepaymentModal from "./RejectRepaymentModal";
import ApproveRepaymentModal from "./ApproveRepaymentModal";
import RepaymentLogsModal from "./RepaymentLogsModal";
import RepaymentCustomFieldModal from "./RepaymentCustomFieldModal";
import useOutsideClick from "../Assets/useOutsideClick";
import PaginationControls from "../pagination/PaginationControls";
import AddRepaymentModal from "./AddRepaymentModal";
import Button, { ButtonType } from "../Assets/Button";
import RepaymentChargesModal from "./EditRepaymentModal";
import RepaymentHistoryCard from "./RepaymentHistoryCard";

interface RepaymentsTableProps {
  repaymentStatus: string;
  from: "account" | "cse";
}

const RepaymentsTable: React.FC<RepaymentsTableProps> = ({
  repaymentStatus = IStatusCycle.PENDING,
  from,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const { repayments, repaymentsTotalPages } = useSelector(
    (state: RootState) => state.commissionAndRepayment,
  );

  const loading = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.fetchingRepaymentsLoading,
  );
  const editRepaymentLoading = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.updateRepaymentLoading,
  );
  const updatingBulk = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.updatingBulkRepaymentLoading,
  );
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>(
    {},
  );
  const [activeSalesRepId, setActiveSalesRepId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState<IRepaymentLog[]>([]);
  const [customFieldModalOpen, setCustomFieldModalOpen] = useState(false);
  const [selectedRepaymentHistoryId, setSelectedRepaymentHistoryId] = useState<
    string | null
  >(null);

  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);

  const [addRepaymentModalOpen, setAddRepaymentModalOpen] = useState(false);

  const [isEditRepaymentModalOpen, setIsEditRepaymentModalOpen] =
    useState(false);
  const [repaymentEditIds, setRepaymentEditIds] = useState<{
    repaymentId: string;
    repaymentHistoryId: string;
  }>();
  const [repaymentDataForEdit, setRepaymentDataForEdit] = useState<{
    fileCharge?: number;
    portalCharge?: number;
    customFields?: ICustomField[];
  }>({
    fileCharge: 0,
    portalCharge: 0,
  });

  const handleAdjustmentSubmit = async (data: {
    fileCharge?: number;
    portalCharge?: number;
    customFields?: ICustomField[];
  }) => {
    if (
      !repaymentEditIds?.repaymentId ||
      !repaymentEditIds.repaymentHistoryId
    ) {
      toast.error("No repayment Ids found");
      return;
    }

    const payload: any = {};

    if (data.fileCharge !== repaymentDataForEdit.fileCharge) {
      payload.fileCharge = data.fileCharge;
    }

    if (data.portalCharge !== repaymentDataForEdit.portalCharge) {
      payload.portalCharge = data.portalCharge;
    }
    const oldCustomFields = JSON.stringify(
      repaymentDataForEdit.customFields || [],
    );

    const newCustomFields = JSON.stringify(data.customFields || []);

    if (oldCustomFields !== newCustomFields) {
      payload.customFields = data.customFields;
    }
    // nothing changed
    if (Object.keys(payload).length === 0) {
      toast.error("No changes detected");
      return;
    }

    try {
      dispatch(
        updateRepayment({
          repaymentId: repaymentEditIds?.repaymentId,
          repaymentHistoryId: repaymentEditIds?.repaymentHistoryId,
          data: payload,
        }),
      ).unwrap();

      // await fetchRepayments(currentPage);
    } catch (error) {
      toast.error("Failed to update commission adjustment");
      console.error(error);
    } finally {
      setIsEditRepaymentModalOpen(false);
      setRepaymentDataForEdit({});
    }
  };

  useOutsideClick(menuRef, () => {
    setActiveMenu(null);
  });

  const itemsPerPage = 10;

  const role = useSelector((state: RootState) => state.auth.role);
  const canUpdateCseStatus = from === "cse";

  const isAccountRole = from === "account";

  const fetchRepayments = useCallback(
    async (page: number = 1) => {
      await dispatch(
        getAllRepayments({
          page,
          limit: itemsPerPage,
          from,
          search,
          status: repaymentStatus,
        }),
      );
    },
    [dispatch, from, search, repaymentStatus],
  );

  useEffect(() => {
    fetchRepayments(currentPage);
  }, [fetchRepayments, currentPage]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleNextPage = async () => {
    if (currentPage >= repaymentsTotalPages) return;
    setIsNextLoading(true);
    const nextPage = currentPage + 1;
    await fetchRepayments(nextPage);
    setCurrentPage(nextPage);
    setIsNextLoading(false);
  };

  const handlePreviousPage = async () => {
    if (currentPage <= 1) return;
    setIsPrevLoading(true);
    const prevPage = currentPage - 1;
    await fetchRepayments(prevPage);
    setCurrentPage(prevPage);
    setIsPrevLoading(false);
  };

  const toggleRow = useCallback((id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleItemSelection = useCallback(
    (repaymentId: string, historyId: string) => {
      const key = `${repaymentId}-${historyId}`;

      setSelectedItems((prev) => {
        const isCurrentlySelected = !!prev[key];

        // If selecting a NEW item
        if (!isCurrentlySelected) {
          // Another sales rep already selected
          if (activeSalesRepId && activeSalesRepId !== repaymentId) {
            toast.error(
              "You can only bulk process repayments of one Sales Representative at a time",
            );
            return prev;
          }

          setActiveSalesRepId(repaymentId);

          return {
            ...prev,
            [key]: true,
          };
        }

        const updated = { ...prev };
        delete updated[key];

        const stillSelected = Object.keys(updated).some((k) =>
          k.startsWith(`${repaymentId}-`),
        );

        if (!stillSelected) {
          setActiveSalesRepId(null);
        }

        return updated;
      });
    },
    [activeSalesRepId],
  );

  const getSelectedRepaymentHistoryIds = useCallback(() => {
    const selectedIds: string[] = [];

    Object.entries(selectedItems).forEach(([key, isSelected]) => {
      if (isSelected) {
        const [, historyId] = key.split("-");
        selectedIds.push(historyId);
      }
    });

    return selectedIds;
  }, [selectedItems]);

  const getSelectableHistoryIds = useCallback(
    (repayment: IRepayment) => {
      if (!Array.isArray(repayment.repaymentHistory)) return [];

      return repayment.repaymentHistory
        .filter((item: IRepaymentHistoryItem) => {
          if (isAccountRole) {
            return (
              item.cseStatus === "approved" && item.accountStatus === "pending"
            );
          }

          if (canUpdateCseStatus) {
            return item.cseStatus !== "approved";
          }

          return false;
        })
        .map((item: IRepaymentHistoryItem) => item._id)
        .filter(Boolean);
    },
    [isAccountRole, canUpdateCseStatus],
  );

  const selectedRepaymentsForModal = useMemo(() => {
    const result: {
      repaymentHistoryId: string;
      customerName?: string;
      repaymentAmount: number;
    }[] = [];

    repayments.forEach((repayment: IRepayment) => {
      if (Array.isArray(repayment.repaymentHistory)) {
        repayment.repaymentHistory.forEach((item: IRepaymentHistoryItem) => {
          const key = `${repayment._id}-${item._id}`;
          if (selectedItems[key]) {
            result.push({
              repaymentHistoryId: item._id!,
              customerName: repayment.payment?.customerName,
              repaymentAmount: item.repaymentAmount || 0,
            });
          }
        });
      }
    });

    return result;
  }, [repayments, selectedItems]);

  const toggleSelectAllForSalesRep = useCallback(
    (repayment: IRepayment) => {
      const repaymentId = repayment._id;
      const selectableIds = getSelectableHistoryIds(repayment);
      if (selectableIds.length === 0) return;

      setSelectedItems((prev) => {
        // prevent multi-sales-rep selection
        if (activeSalesRepId && activeSalesRepId !== repaymentId) {
          toast.error(
            "You can only bulk process repayments of one Sales Representative at a time",
          );
          return prev;
        }

        const allSelected = selectableIds.every(
          (hid: string | undefined) => prev[`${repaymentId}-${hid}`],
        );

        const updated = { ...prev };

        if (allSelected) {
          // deselect all
          selectableIds.forEach((hid: string | undefined) => {
            delete updated[`${repaymentId}-${hid}`];
          });

          setActiveSalesRepId(null);
        } else {
          // select all
          selectableIds.forEach((hid: string | undefined) => {
            updated[`${repaymentId}-${hid}`] = true;
          });

          setActiveSalesRepId(repaymentId);
        }

        return updated;
      });
    },
    [activeSalesRepId, getSelectableHistoryIds],
  );

  const handleBulkAction = useCallback(
    async (action: "approve" | "reject", proofFile?: File | null) => {
      const selectedIds = getSelectedRepaymentHistoryIds();

      if (selectedIds.length === 0) {
        toast.error("Please select at least one repayment");
        return;
      }

      try {
        const statusType = canUpdateCseStatus ? "cse" : "account";
        await dispatch(
          updateBulkRepayments({
            repaymentHistoryIds: selectedIds,
            action,
            role: role ?? "",
            statusType,
            ...(action === "approve" && { proofFile }),
          }),
        ).unwrap();

        if (action === "approve") {
          await dispatch(
            getAllRepayments({
              page: currentPage,
              limit: itemsPerPage,
              from,
            }),
          );
        }

        await fetchRepayments(currentPage);

        setSelectedItems({});
        setActiveSalesRepId(null);
      } catch (error) {
        console.error("Failed to update repayments:", error);
      }
    },
    [
      canUpdateCseStatus,
      currentPage,
      dispatch,
      fetchRepayments,
      from,
      getSelectedRepaymentHistoryIds,
      role,
    ],
  );

  const handleRejectSubmit = async (remark: string) => {
    const selectedIds = getSelectedRepaymentHistoryIds();

    try {
      const statusType = canUpdateCseStatus ? "cse" : "account";

      await dispatch(
        updateBulkRepayments({
          repaymentHistoryIds: selectedIds,
          action: "reject",
          role: role ?? "",
          remark,
          statusType,
        }),
      ).unwrap();

      await dispatch(
        getAllRepayments({
          page: currentPage,
          limit: itemsPerPage,
          from,
        }),
      );

      setRejectModalOpen(false);
      setSelectedItems({});
      setActiveSalesRepId(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCustomFieldSubmit = async (customFields: ICustomField[]) => {
    if (!selectedRepaymentHistoryId) return;

    try {
      await dispatch(
        updateRepaymentCustomField({
          repaymentHistoryId: selectedRepaymentHistoryId,
          customFields,
        }),
      ).unwrap();

      setCustomFieldModalOpen(false);
      setSelectedRepaymentHistoryId(null);
    } catch (error) {
      console.error("Failed to update custom fields:", error);
    }
  };

  const handleApproveClick = () => {
    if (isAccountRole) {
      setApproveModalOpen(true);
    } else {
      handleBulkAction("approve");
    }
  };

  const handleApproveConfirm = async (file?: File | null) => {
    setApproveModalOpen(false);
    await handleBulkAction("approve", file);
  };

  const getStatusBadge = (status: string, label?: string) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    if (status === "approved") {
      return (
        <span
          className={`${baseClasses} bg-emerald-100 text-emerald-800 border border-emerald-200`}
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          {label ? `Approved by ${label}` : "Approved"}
        </span>
      );
    } else if (status === "rejected") {
      return (
        <span
          className={`${baseClasses} bg-red-100 text-red-800 border border-red-200`}
        >
          <XCircle className="w-3 h-3 mr-1" />
          {label ? `Rejected by ${label}` : "Rejected"}
        </span>
      );
    } else {
      return (
        <span
          className={`${baseClasses} bg-amber-100 text-amber-800 border border-amber-200`}
        >
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          {label ? `Pending by ${label}` : "Pending"}
        </span>
      );
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹ ${amount.toLocaleString("en-IN")}`;
  };

  const calculateRepaymentTotals = useCallback(() => {
    let totalRepaymentAmount = 0;
    let totalHistoryItems = 0;

    repayments.forEach((repayment: IRepayment) => {
      if (Array.isArray(repayment.repaymentHistory)) {
        repayment.repaymentHistory.forEach((item) => {
          totalRepaymentAmount += item.repaymentAmount || 0;
          totalHistoryItems++;
        });
      }
    });

    return { totalRepaymentAmount, totalHistoryItems };
  }, [repayments]);

  const { totalRepaymentAmount } = calculateRepaymentTotals();

  const calculateTotals = useCallback(() => {
    let totalSelected = 0;
    let totalSelectedAmount = 0;
    let totalAllAmount = 0;

    repayments.forEach((repayment: IRepayment) => {
      if (Array.isArray(repayment.repaymentHistory)) {
        repayment.repaymentHistory.forEach((item) => {
          const amount = item.repaymentAmount || 0;
          totalAllAmount += amount;

          const key = `${repayment._id}-${item._id}`;
          if (selectedItems[key]) {
            totalSelected++;
            totalSelectedAmount += amount;
          }
        });
      }
    });

    return { totalSelected, totalSelectedAmount, totalAllAmount };
  }, [repayments, selectedItems]);

  const { totalSelected, totalSelectedAmount } = calculateTotals();

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
      {/* Header with Search */}
      <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <SearchInput
          placeholder="Search By Sales Representative Or Customer..."
          className="w-full pr-4 py-2 bg-white border border-gray-200 rounded-md
              focus:ring-1 focus:ring-navyBlue-primary focus:border-navyBlue-primary
              outline-none transition-all text-sm text-gray-700
              placeholder:text-gray-400 shadow-sm"
          onSearch={handleSearch}
        />

        {role === "admin" && (
          <Button
            type={ButtonType.Submit}
            onClick={() => setAddRepaymentModalOpen(true)}
            className="text-sm "
          >
            <PlusCircleIcon />
          </Button>
        )}

        {/* Summary Card */}
        <div className="w-full xl:w-auto rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 to-white px-4 py-3">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-600">Total Repayments:</span>
            <span className="font-semibold text-sm text-sky-700">
              {formatCurrency(totalRepaymentAmount)}
            </span>
          </div>
        </div>
      </div>

      {totalSelected > 0 && (
        <div className="mb-4 p-3 bg-gradient-to-r from-sky-50 to-white border border-sky-100 rounded-xl">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="text-sm text-sky-700">
              <span className="font-medium">
                {totalSelected} item(s) selected
              </span>
              {totalSelectedAmount > 0 && (
                <span className="ml-4 text-slate-600">
                  Total: {formatCurrency(totalSelectedAmount)}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleApproveClick}
                disabled={updatingBulk}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                  updatingBulk
                    ? "bg-emerald-200 cursor-not-allowed text-white"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                {updatingBulk ? "Processing..." : "Approve"}
              </button>

              <button
                onClick={() => setRejectModalOpen(true)}
                disabled={updatingBulk}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                  updatingBulk
                    ? "bg-red-200 cursor-not-allowed text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                <XCircle className="w-4 h-4" />
                {updatingBulk ? "Processing..." : "Reject"}
              </button>

              <button
                onClick={() => {
                  setSelectedItems({});
                  setActiveSalesRepId(null);
                }}
                className="px-3 py-2 text-xs text-slate-600 hover:text-slate-800 border border-slate-300 hover:border-slate-500 rounded-md transition-all"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Repayments Table */}
      <div className="hidden lg:block overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full divide-y divide-slate-100">
          <thead>
            <tr className="text-left text-sm text-slate-500">
              <th className="px-4 py-3"></th>
              <th className="px-4 py-3">Sales Rep</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Mobile</th>
              <th className="px-4 py-3">Payment Count</th>
              <th className="px-4 py-3">Total Repayment</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-slate-100">
            {repayments && repayments.length > 0 ? (
              repayments.map((repayment: IRepayment) => {
                const salesRep = repayment.salesRep || {};

                const repaymentHistory = Array.isArray(
                  repayment.repaymentHistory,
                )
                  ? repayment.repaymentHistory
                  : [];

                const totalRepaymentForRep = repaymentHistory.reduce(
                  (sum: number, item: IRepaymentHistoryItem) =>
                    sum + (item.repaymentAmount || 0),
                  0,
                );

                const id = repayment._id;

                return (
                  <React.Fragment key={id}>
                    {/* Main Row */}
                    <tr
                      onClick={() =>
                        repaymentHistory.length > 0 && toggleRow(id)
                      }
                      className={`cursor-pointer transition ${
                        repaymentHistory.length > 0
                          ? "hover:bg-slate-50"
                          : "cursor-not-allowed opacity-60"
                      }`}
                    >
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSelectAllForSalesRep(repayment);
                            }}
                            className="p-1 rounded-md hover:bg-slate-100"
                            title="Select all repayments for this Sales Rep"
                          >
                            {getSelectableHistoryIds(repayment).length > 0 &&
                            getSelectableHistoryIds(repayment).every(
                              (hid: string | undefined) =>
                                selectedItems[`${repayment._id}-${hid}`],
                            ) ? (
                              <CheckSquare className="w-5 h-5 text-sky-600" />
                            ) : (
                              <Square className="w-5 h-5 text-slate-300 hover:text-slate-500" />
                            )}
                          </button>
                          <div className="flex flex-wrap gap-2">
                            <div>
                              <div className="text-sm text-slate-800">
                                <span className="font-semibold">
                                  {salesRep.companyName || "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold">
                          {salesRep.name || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold italic">
                          {repayment.payment?.customerName ??
                            "Unknown Customer"}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <Phone className="w-3 h-3 text-slate-400" />
                          {salesRep.mobileNumber || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <FileText className="w-3 h-3 text-slate-400" />
                          {repaymentHistory.length} payment(s)
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                          <CreditCard className="w-3 h-3 text-slate-400" />
                          {formatCurrency(totalRepaymentForRep)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(id);
                          }}
                          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 focus:outline-none"
                          aria-expanded={!!expanded[id]}
                          disabled={repaymentHistory.length === 0}
                        >
                          {expanded[id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Row - Repayment History */}
                    {expanded[id] && repaymentHistory.length > 0 && (
                      <tr>
                        <td colSpan={7} className="bg-slate-50 p-3 sm:p-4">
                          <div className="space-y-3">
                            {/* Customer Info Card */}
                            <div className="bg-white rounded-lg p-3 border border-slate-200 mb-3">
                              <div className="flex flex-wrap items-center gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium text-slate-700">
                                    {repayment.payment?.customerName || "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 text-slate-400" />
                                  <span className="text-slate-600">
                                    {repayment.payment?.customerMobileNumber ||
                                      "N/A"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Repayment History Grid */}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                              {repaymentHistory.map((item, idx) => (
                                <RepaymentHistoryCard
                                  key={item._id || idx}
                                  item={item}
                                  idx={idx}
                                  id={id}
                                  repayment={repayment}
                                  selectedItems={selectedItems}
                                  activeMenu={activeMenu}
                                  menuRef={menuRef}
                                  canUpdateCseStatus={canUpdateCseStatus}
                                  isAccountRole={isAccountRole}
                                  toggleItemSelection={toggleItemSelection}
                                  setRepaymentEditIds={setRepaymentEditIds}
                                  setRepaymentDataForEdit={
                                    setRepaymentDataForEdit
                                  }
                                  setIsEditRepaymentModalOpen={
                                    setIsEditRepaymentModalOpen
                                  }
                                  setActiveMenu={setActiveMenu}
                                  setSelectedRepaymentHistoryId={
                                    setSelectedRepaymentHistoryId
                                  }
                                  setCustomFieldModalOpen={
                                    setCustomFieldModalOpen
                                  }
                                  setSelectedLogs={setSelectedLogs}
                                  setLogsModalOpen={setLogsModalOpen}
                                  getStatusBadge={getStatusBadge}
                                  formatCurrency={formatCurrency}
                                />
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-500"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Loading repayments...</span>
                    </div>
                  ) : (
                    "No repayments found."
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* MOBILE VIEW */}
      <div className="space-y-3 lg:hidden">
        {repayments && repayments.length > 0 ? (
          repayments.map((repayment: IRepayment) => {
            const salesRep = repayment.salesRep || {};

            const repaymentHistory = Array.isArray(repayment.repaymentHistory)
              ? repayment.repaymentHistory
              : [];

            const totalRepaymentForRep = repaymentHistory.reduce(
              (sum: number, item: IRepaymentHistoryItem) =>
                sum + (item.repaymentAmount || 0),
              0,
            );

            const id = repayment._id;

            return (
              <div
                key={id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                {/* Header */}
                <div
                  onClick={() => repaymentHistory.length > 0 && toggleRow(id)}
                  className="p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-slate-800">
                        {salesRep.companyName || "-"}
                      </h3>

                      <p className="mt-1 text-xs text-slate-500">
                        {salesRep.name || "-"}
                      </p>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRow(id);
                      }}
                      className="rounded-lg p-1.5 hover:bg-slate-100"
                    >
                      {expanded[id] ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Customer</p>

                      <p className="mt-1 truncate text-xs font-medium text-slate-700">
                        {repayment.payment?.customerName || "N/A"}
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Repayment</p>

                      <p className="mt-1 text-sm font-bold text-emerald-600">
                        {formatCurrency(totalRepaymentForRep)}
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Mobile</p>

                      <p className="mt-1 text-xs font-medium text-slate-700">
                        {salesRep.mobileNumber || "-"}
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-3">
                      <p className="text-[11px] text-slate-500">Payments</p>

                      <p className="mt-1 text-xs font-medium text-slate-700">
                        {repaymentHistory.length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expanded */}
                {expanded[id] && repaymentHistory.length > 0 && (
                  <div className="border-t border-slate-100 bg-slate-50 p-3">
                    <div className="grid grid-cols-1 gap-3">
                      {repaymentHistory.map((item, idx) => (
                        <RepaymentHistoryCard
                          key={item._id || idx}
                          item={item}
                          idx={idx}
                          id={id}
                          repayment={repayment}
                          selectedItems={selectedItems}
                          activeMenu={activeMenu}
                          menuRef={menuRef}
                          canUpdateCseStatus={canUpdateCseStatus}
                          isAccountRole={isAccountRole}
                          toggleItemSelection={toggleItemSelection}
                          setRepaymentEditIds={setRepaymentEditIds}
                          setRepaymentDataForEdit={setRepaymentDataForEdit}
                          setIsEditRepaymentModalOpen={
                            setIsEditRepaymentModalOpen
                          }
                          setActiveMenu={setActiveMenu}
                          setSelectedRepaymentHistoryId={
                            setSelectedRepaymentHistoryId
                          }
                          setCustomFieldModalOpen={setCustomFieldModalOpen}
                          setSelectedLogs={setSelectedLogs}
                          setLogsModalOpen={setLogsModalOpen}
                          getStatusBadge={getStatusBadge}
                          formatCurrency={formatCurrency}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            {loading ? "Loading repayments..." : "No repayments found."}
          </div>
        )}
      </div>
      <div className="mt-4">
        <PaginationControls
          isNextFetching={isNextLoading}
          isPrevFetching={isPrevLoading}
          currentPage={currentPage}
          totalPages={repaymentsTotalPages}
          onNext={handleNextPage}
          onPrev={handlePreviousPage}
        />
      </div>

      <ApproveRepaymentModal
        open={approveModalOpen}
        repayments={selectedRepaymentsForModal}
        loading={updatingBulk}
        onClose={() => setApproveModalOpen(false)}
        onConfirm={handleApproveConfirm}
      />
      <RejectRepaymentModal
        open={rejectModalOpen}
        loading={updatingBulk}
        onClose={() => setRejectModalOpen(false)}
        onSubmit={handleRejectSubmit}
      />
      <RepaymentLogsModal
        open={logsModalOpen}
        logs={selectedLogs}
        onClose={() => setLogsModalOpen(false)}
      />
      <RepaymentCustomFieldModal
        open={customFieldModalOpen}
        loading={updatingBulk}
        onClose={() => {
          setCustomFieldModalOpen(false);
          setSelectedRepaymentHistoryId(null);
        }}
        onSubmit={handleCustomFieldSubmit}
      />
      <AddRepaymentModal
        open={addRepaymentModalOpen}
        onClose={() => setAddRepaymentModalOpen(false)}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        search={search}
        status={repaymentStatus}
        from={from}
      />
      <RepaymentChargesModal
        isOpen={isEditRepaymentModalOpen}
        isLoading={editRepaymentLoading}
        values={repaymentDataForEdit}
        onClose={() => {
          setIsEditRepaymentModalOpen(false);
          setRepaymentDataForEdit({
            fileCharge: 0,
            portalCharge: 0,
          });
        }}
        onSubmit={handleAdjustmentSubmit}
      />
    </div>
  );
};

export default RepaymentsTable;
